// Session AU, Item 3: a shared, per-page client-side column-sort controller for the office list
// tables. The registers (Customers / Sites / Lifts) already sort client-side via RegisterPage's
// sortValue accessors (Session AD); this hook adds the missing sessionStorage PERSISTENCE (the sort a
// page is left on is restored for the session, cleared on browser close, never stored server-side) and
// is reused by the standalone list tables (Contracts, Defects, Service records, Projects, Renewals) via
// the SortableHeader component. The sort semantics (three-step asc -> desc -> default cycle, nulls last,
// numeric-aware compare) are the SAME everywhere: this reuses registerTable's pure primitives, so there
// is one source of truth for the comparison.
//
// NOTE: this is deliberately client-side, not server-side ?sort=&dir= query params. No office list is
// paginated (every list endpoint returns all rows), computed columns (next-service-due, days-to-renewal)
// have no ORDER BY-able stored field, and RegisterPage already sorts client-side; a parallel server-side
// path would duplicate the working mechanism. See docs/HANDOFF-SESSION-AU-COMPLETE.md.
import { useCallback, useEffect, useState } from "react";
import { compareSortValues, nextSortState } from "../routes/office/register/registerTable";
import type { SortDir } from "../routes/office/register/registerTable";

export type { SortDir };
export interface SortState {
  sortKey: string | null; // the active sort column key, or null for the default (load) order
  sortDir: SortDir;
}

const storageKey = (page: string): string => `sort:${page}`;

// Read a persisted sort state for a page, tolerating a missing/malformed value (returns null then).
export function readStoredSort(page: string): SortState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(storageKey(page));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SortState>;
    const dir = parsed.sortDir;
    // Only a non-null active column is ever persisted (the default clears the stored value), so a
    // valid stored state has a string key and a known direction.
    if ((dir === "asc" || dir === "desc") && typeof parsed.sortKey === "string" && parsed.sortKey) {
      return { sortKey: parsed.sortKey, sortDir: dir };
    }
  } catch {
    // malformed storage -> ignore
  }
  return null;
}

function writeStoredSort(page: string, state: SortState): void {
  if (typeof window === "undefined") return;
  try {
    // The default (no active column) is the absence of a stored value, so it clears on the third click.
    if (state.sortKey === null) window.sessionStorage.removeItem(storageKey(page));
    else window.sessionStorage.setItem(storageKey(page), JSON.stringify(state));
  } catch {
    // private mode / quota -> the sort still works this session, just is not persisted
  }
}

export interface SortableColumnApi extends SortState {
  // Clicking a header: asc -> desc -> default (three-step cycle) for that column.
  toggle: (key: string) => void;
  // Set the sort directly (e.g. a named "Sort by" preset).
  setSort: (key: string | null, dir: SortDir) => void;
}

// The per-page sort controller. `page` is a stable, unique storage key for the page.
export function useSortableColumn(page: string): SortableColumnApi {
  const [state, setState] = useState<SortState>(() => readStoredSort(page) ?? { sortKey: null, sortDir: "asc" });
  useEffect(() => {
    writeStoredSort(page, state);
  }, [page, state]);
  const toggle = useCallback((key: string) => setState((s) => nextSortState(s, key)), []);
  const setSort = useCallback((key: string | null, dir: SortDir) => setState({ sortKey: key, sortDir: dir }), []);
  return { ...state, toggle, setSort };
}

// A sort-value accessor per column key (the value to compare a row by for that column).
export type SortAccessors<T> = Record<string, (row: T) => string | number | null | undefined>;

// Sort a list of rows by the active sort state using the provided accessors. Nulls/blanks sort LAST in
// both directions (compareSortValues pins empties last, so a desc sort never floats blanks to the top);
// text compares case-insensitively and numeric-aware ("ELE-9" before "ELE-10"). Stable (ties keep their
// incoming order). Returns a NEW array; never mutates. No active column -> the rows unchanged. Pure.
export function sortRows<T>(rows: T[], accessors: SortAccessors<T>, state: SortState): T[] {
  if (!state.sortKey) return rows;
  const accessor = accessors[state.sortKey];
  if (!accessor) return rows;
  const sign = state.sortDir === "desc" ? -1 : 1;
  return rows
    .map((row, i) => ({ row, i }))
    .sort((x, y) => {
      const av = accessor(x.row);
      const bv = accessor(y.row);
      const aEmpty = av == null || av === "";
      const bEmpty = bv == null || bv === "";
      const cmp = compareSortValues(av, bv);
      const directed = aEmpty || bEmpty ? cmp : cmp * sign;
      return directed || x.i - y.i;
    })
    .map((w) => w.row);
}
